import {
    Alert,
    CardHeader,
    Snackbar,
    SnackbarCloseReason,
    Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiWalletTransaction } from '../../../../api/hexa/ApiWallet';
import { WalletTransaction } from '../../../../api/hexa/models/WalletModels';

interface WalletListProps {
    customerId: string;
    refreshTrigger?: number;
}

const WalletTransactionList = ({ customerId, refreshTrigger }: WalletListProps) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<WalletTransaction[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'id', sort: 'desc' }
    ]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });
    const [error, setError] = useState({
        open: false,
        message: '',
    });
    /**
     * Define the columns for the DataGrid
     */
    const columns = useMemo<GridColDef<WalletTransaction>[]>(
        () => [
            { field: 'id', headerName: 'ID', flex: 1, maxWidth: 100 },
            { field: 'amount', headerName: 'Amount', flex: 2 },
            { field: 'transaction_type', headerName: 'Transaction Type', flex: 2 },
            { field: 'description', headerName: 'Description', flex: 2 },
            {
                field: 'created',
                headerName: 'Date',
                type: 'date',
                flex: 1,
                valueGetter: (value, row) => (row.created ? new Date(row.created) : ''),
                renderCell: (params: any) =>
                    new Date(params.row.created).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    }),
            },
        ],
        []
    );

    /**
     * Fetch transactions from the API and set the rows state
     */
    const fetchWalletTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const { total, transactions } = await ApiWalletTransaction.index(
                customerId,
                {
                    page: paginationModel.page,
                    size: paginationModel.pageSize,
                    sort_field: sortModel[0]?.field ?? 'id',
                    sort_direction: sortModel[0]?.sort ?? 'desc',
                }
            );
            setRowCount(total);
            setRows(transactions);
        } catch (error: any) {
            setError({
                open: true,
                message: `Error ${error.status ?? 500} fetching the transactions: 
        ${error.response?.data.error ?? 'Error fetching the transactions'}`,
            });
        } finally {
            setLoading(false);
        }
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        sortModel,
        customerId
    ]);

    useEffect(() => {
        fetchWalletTransactions();
    }, [fetchWalletTransactions, refreshTrigger]);

    /**
     * Close the error snackbar
     */
    const handleSnackbarClose = (
        event: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setError({
            open: false,
            message: error.message,
        });
    };

    return (
        <>
            <CardHeader
                title={
                    <Typography variant="h5">
                        {t('wallet_transaction.title')}
                    </Typography>
                }
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    getRowId={(row) => row.id}
                    loading={loading}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    rowCount={rowCount}
                    paginationMode="server"
                    pagination={true}
                    pageSizeOptions={[10, 25, 50, 100]}
                    autoHeight={true}
                    rows={rows}
                    columns={columns}
                    density={'compact'}
                    rowHeight={55}
                    sx={{
                        marginBottom: '2.5rem',
                        marginX: '1rem',
                    }}
                />
            </div>
            <Snackbar
                open={error.open}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="error">
                    {error.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default WalletTransactionList;
